import { useRef, useEffect, useCallback, useMemo } from 'react';
import { format, addDays, isBefore, startOfDay } from 'date-fns';
import { TaskStatus } from '@signalvote/shared';
import { useCalendarStore } from '@/stores/calendarStore';
import { useCampaignStore } from '@/stores/campaignStore';
import { useTaskStore } from '@/stores';
import { useCalendarData } from '@/hooks/useCalendarData';
import { useCalendarDialogs } from '@/hooks/useCalendarDialogs';
import { useMayManageTasks } from '@/hooks';
import { useUndoDelete } from '@/hooks/useUndoDelete';
import { useCompleteTask, useReopenTask, useUpdateTask } from '@/hooks/queries';
import { useAuth } from '@/contexts/AuthContext';
import { HOUR_HEIGHT, isToday, isSameDay, getCurrentTimePartsInTimezone, getCampaignToday } from '@/lib/calendarUtils';
import { TimeSlot } from '../shared/TimeSlot';
import { DayColumn } from '../shared/DayColumn';
import { AllDayRow } from '../AllDayRow';
import { CurrentTimeIndicator } from '../CurrentTimeIndicator';
import { ShiftCreateDialog } from '../ShiftCreateDialog';
import { ShiftDetailsDialog } from '../ShiftDetailsDialog';
import { CreateChoiceDialog } from '../CreateChoiceDialog';
import { TaskCreateDialog } from '../TaskCreateDialog';
import { TaskDetailsDialog } from '../TaskDetailsDialog';
import { CalendarEventDetailsDialog } from '../CalendarEventDetailsDialog';
import { CalendarTimezoneBanner } from '../CalendarTimezoneBanner';

const TIME_GUTTER_WIDTH = 60;
const HOURS_IN_DAY = 24;

/**
 * Main calendar grid with CSS Grid layout.
 * Layout: time gutter (60px) + 7 equal day columns.
 * Rows: all-day row (auto) + 24 hour rows (80px each).
 */
export function WeekView() {
  const containerRef = useRef<HTMLDivElement>(null);
  const visibleStartDate = useCalendarStore((state) => state.visibleStartDate);
  const selectedDate = useCalendarStore((state) => state.selectedDate);
  const timeZoneId = useCampaignStore((s) => s.selectedCampaign?.timeZoneId);
  const isViewingToday = useCalendarStore((state) => state.isViewingToday);
  const shiftStatusFilter = useCalendarStore((state) => state.shiftStatusFilter);
  const showDelegated = useCalendarStore((state) => state.showDelegated);
  const { user } = useAuth();
  const mayManageTasks = useMayManageTasks();

  // Compute 7 visible days starting from visibleStartDate
  const visibleDays = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(visibleStartDate, i));
    }
    return days;
  }, [visibleStartDate]);

  const {
    getShiftsForDate,
    getTasksForDate,
    getCalendarEventsForDate,
    allDayEventsByDate,
    keyDatesByDate,
    getShiftStatus,
    getClaimedBy,
    parseShiftStatus,
  } = useCalendarData(visibleDays[0], visibleDays[visibleDays.length - 1], timeZoneId);

  // Subscribe to tasks array so component re-renders when tasks change
  const _tasks = useTaskStore((state) => state.tasks);
  const updateTaskStatus = useTaskStore((state) => state.updateTaskStatus);
  const updateTask = useTaskStore((state) => state.updateTask);
  const activeFilters = useCalendarStore((state) => state.activeFilters);

  const {
    shiftCreateDialog,
    shiftDetailsDialog,
    taskCreateDialog,
    taskDetailsDialog,
    calendarEventDetailsDialog,
    choiceDialog,
    handleShiftClick,
    handleTaskClick,
    handleCalendarEventClick,
    handleEmptySlotClick,
    handleChooseShift,
    handleChooseTask,
  } = useCalendarDialogs();

  const { requestDelete, undoDelete, isSnackbarOpen, pendingTaskTitle } = useUndoDelete();

  const { mutate: completeTask } = useCompleteTask();
  const { mutate: reopenTask } = useReopenTask();
  const { mutate: updateTaskMutate } = useUpdateTask();

  const handleTaskResize = useCallback(
    (taskId: string, newDuration: number) => {
      // Optimistic: update Zustand immediately
      updateTask(taskId, { durationMinutes: newDuration });
      // Persist to API
      updateTaskMutate({ id: taskId, data: { durationMinutes: newDuration } });
    },
    [updateTask, updateTaskMutate]
  );

  const handleTaskCheckboxClick = useCallback(
    (taskId: string) => {
      const tasks = useTaskStore.getState().tasks;
      const task = tasks.find((t) => t.id === taskId);
      if (task) {
        const newStatus =
          task.status === TaskStatus.Completed
            ? TaskStatus.Pending
            : TaskStatus.Completed;
        // Optimistic: update Zustand immediately
        updateTaskStatus(taskId, newStatus);
        // Persist to API
        if (newStatus === TaskStatus.Completed) {
          completeTask(taskId);
        } else {
          reopenTask(taskId);
        }
      }
    },
    [updateTaskStatus, completeTask, reopenTask]
  );

  // Scroll to current time on mount when viewing today
  useEffect(() => {
    if (containerRef.current && isViewingToday()) {
      const { hours: currentHour } = getCurrentTimePartsInTimezone(timeZoneId);
      // Scroll to show current time in view, with some padding above
      const scrollPosition = Math.max(0, (currentHour - 2) * HOUR_HEIGHT);
      containerRef.current.scrollTop = scrollPosition;
    }
  }, [isViewingToday, timeZoneId]);

  const hours = Array.from({ length: HOURS_IN_DAY }, (_, i) => i);

  return (
      <div
        ref={containerRef}
        className="w-full h-full overflow-auto relative flex-1"
      >
        <CalendarTimezoneBanner />
        {/* Main Grid Container */}
        <div
          className="grid min-w-[800px]"
          style={{
            gridTemplateColumns: `${TIME_GUTTER_WIDTH}px repeat(7, 1fr)`,
            gridTemplateRows: `auto repeat(${HOURS_IN_DAY}, ${HOUR_HEIGHT}px)`,
          }}
        >
          {/* All-Day Row (sticky) */}
          <AllDayRow visibleDays={visibleDays} allDayEventsByDate={allDayEventsByDate} keyDatesByDate={keyDatesByDate} onEventClick={handleCalendarEventClick} selectedDate={selectedDate} />

          {/* Time Gutter Column */}
          <div
            className="flex flex-col border-r border-dotted border-gray-200 bg-white"
            style={{
              gridColumn: 1,
              gridRow: `2 / span ${HOURS_IN_DAY}`,
            }}
          >
            {hours.map((hour) => (
              <TimeSlot key={hour} hour={hour} />
            ))}
          </div>

          {/* Day Columns */}
          {visibleDays.map((date, index) => {
            const dateStr = format(date, 'yyyy-MM-dd');
            const allShiftsForDate = getShiftsForDate(dateStr);
            const allTasksForDate = getTasksForDate(dateStr);
            const allEventsForDate = getCalendarEventsForDate(dateStr);
            const todayColumn = isToday(date);
            const pastColumn = !todayColumn && isBefore(startOfDay(date), getCampaignToday());
            const selectedColumn = isSameDay(date, selectedDate);

            // Filter shifts based on selected status filter
            const filteredShifts =
              shiftStatusFilter === 'All'
                ? allShiftsForDate
                : allShiftsForDate.filter(
                    (shift) => parseShiftStatus(shift.status) === shiftStatusFilter
                  );

            // Apply category filters from CalendarFilterBar
            const isFilteringAll = activeFilters.includes('all');
            const categoryFilters = activeFilters.filter((f) => f !== 'shifts' && f !== 'calendar');
            const showShifts = isFilteringAll || activeFilters.includes('shifts');
            const showCalendarEvents = isFilteringAll || activeFilters.includes('calendar');

            const categoryFilteredTasks = isFilteringAll
              ? allTasksForDate
              : allTasksForDate.filter((t) => {
                  if (categoryFilters.length === 0) return true;
                  if (categoryFilters.includes('uncategorized') && !t.categoryId) return true;
                  if (t.categoryId && categoryFilters.includes(t.categoryId)) return true;
                  return false;
                });

            // Apply delegated filter: hide tasks I created and assigned to others
            const filteredTasks = showDelegated
              ? categoryFilteredTasks
              : categoryFilteredTasks.filter(
                  (t) => !(t.createdById === user?.id && t.assignedToId && t.assignedToId !== user?.id)
                );

            const tasksForDate = filteredTasks;
            const eventsForDate = showCalendarEvents ? allEventsForDate : [];
            const visibleShifts = showShifts ? filteredShifts : [];

            // Count only scheduled tasks (with dueTime)
            const scheduledTaskCount = tasksForDate.filter((t) => t.dueTime).length;

            return (
              <div
                key={date.toISOString()}
                style={{
                  gridColumn: index + 2, // Columns 2-8 (1-indexed, after time gutter)
                  gridRow: `2 / span ${HOURS_IN_DAY}`,
                }}
              >
                <DayColumn
                  date={date}
                  isToday={todayColumn}
                  isPast={pastColumn}
                  isSelected={selectedColumn}
                  shiftCount={visibleShifts.length}
                  taskCount={scheduledTaskCount}
                  shiftsForDay={visibleShifts}
                  tasksForDay={tasksForDate}
                  calendarEvents={eventsForDate}
                  timeZoneId={timeZoneId}
                  getShiftStatus={getShiftStatus}
                  getClaimedBy={getClaimedBy}
                  onShiftClick={handleShiftClick}
                  onEmptySlotClick={handleEmptySlotClick}
                  onTaskCheckboxClick={handleTaskCheckboxClick}
                  onTaskResize={mayManageTasks ? handleTaskResize : undefined}
                  onTaskClick={handleTaskClick}
                  onCalendarEventClick={handleCalendarEventClick}
                >
                  {/* Current time indicator - show in all columns when viewing today */}
                  {isViewingToday() && (
                    <CurrentTimeIndicator
                      isToday={todayColumn}
                      showTimeLabel={index === 0}
                      timeZoneId={timeZoneId}
                    />
                  )}
                </DayColumn>
              </div>
            );
          })}
        </div>

        <CreateChoiceDialog
          open={choiceDialog.isOpen}
          onClose={choiceDialog.close}
          onChooseShift={handleChooseShift}
          onChooseTask={handleChooseTask}
        />

        {shiftCreateDialog.isOpen && shiftCreateDialog.data && (
          <ShiftCreateDialog
            open={shiftCreateDialog.isOpen}
            onClose={shiftCreateDialog.close}
            initialDate={shiftCreateDialog.data.date}
            initialHour={shiftCreateDialog.data.hour}
          />
        )}

        {shiftDetailsDialog.isOpen && shiftDetailsDialog.data && (
          <ShiftDetailsDialog
            open={shiftDetailsDialog.isOpen}
            onClose={shiftDetailsDialog.close}
            shiftId={shiftDetailsDialog.data.shiftId}
          />
        )}

        {taskCreateDialog.isOpen && taskCreateDialog.data && (
          <TaskCreateDialog
            open={taskCreateDialog.isOpen}
            onClose={taskCreateDialog.close}
            initialDate={taskCreateDialog.data.date}
            initialTime={`${taskCreateDialog.data.hour.toString().padStart(2, '0')}:00`}
          />
        )}

        <TaskDetailsDialog
          open={taskDetailsDialog.isOpen}
          onClose={taskDetailsDialog.close}
          task={taskDetailsDialog.data?.task ?? null}
          onDelete={requestDelete}
        />

        <CalendarEventDetailsDialog
          open={calendarEventDetailsDialog.isOpen}
          onClose={calendarEventDetailsDialog.close}
          event={calendarEventDetailsDialog.data?.event ?? null}
          timeZoneId={timeZoneId}
        />

        {isSnackbarOpen && (
          <div className="fixed bottom-4 left-4 z-50 flex items-center gap-4 bg-foreground text-background px-4 py-3 rounded-md shadow-lg">
            <span>Task "{pendingTaskTitle}" deleted</span>
            <button
              onClick={undoDelete}
              className="text-secondary font-medium uppercase text-sm hover:underline"
            >
              Undo
            </button>
          </div>
        )}
      </div>
  );
}

export default WeekView;
